import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import techImage from '../assets/images/2.jpg';
import nuroImage from '../assets/images/1.jpg';
import oncologyImage from '../assets/images/oncology.jpg';
import rareDiseases from '../assets/images/rare.jpg';
import immunImage from '../assets/images/immun.jpg';
import geneImage from '../assets/images/gene.jpg';
import equpImage from '../assets/images/equp.jpg';
import cardioImage from '../assets/images/cardio.jpg';
import infuImage from '../assets/images/infu.jpg';
import dermImage from '../assets/images/derm.jpg';
import digestiveImage from '../assets/images/digestive.jpg';
import optoImage from '../assets/images/opto.jpg';
import lungImage from '../assets/images/lung.jpg';
import { 
  FlaskConical, 
  Database, 
  FileCheck, 
  PenTool, 
  Shield, 
  Paste,
  AlertTriangle,
  ArrowRight,
  CheckCircle,
  Globe,
  Users,
  Award,
  Zap,
  TrendingUp,
  Building2,
  Target,
  Clock,
  BarChart3
} from 'lucide-react';

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg";
const TECH_IMAGE = techImage; 
const AI_IMAGE = "https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/vvnjwp1b_abstract-polygonal-head-outline-on-dark-backdrop-w-2025-10-15-01-46-07-utc.jpg";
import capabilitiesPDF from '../assets/pdf/vetforce-solutions-cabalilities.pdf';

const services = [
  { icon: FlaskConical, title: 'Clinical Trial Management', description: 'End-to-end trial management from study initiation to closeout with real-time analytics and risk-based monitoring.', link: '/services#clinical-trial' },
  { icon: Database, title: 'Biostatistics & Data', description: 'Advanced biostatistics and data management ensuring data integrity and regulatory compliance.', link: '/services#biostatistics' },
  { icon: FileCheck, title: 'Regulatory Affairs', description: 'Strategic guidance for IND, NDA, BLA, and global regulatory submissions.', link: '/services#regulatory' },
  { icon: PenTool, title: 'Medical Writing', description: 'High-quality clinical and regulatory documents meeting global standards.', link: '/services#medical-writing' },
  { icon: Shield, title: 'Pharmacovigilance', description: 'Comprehensive safety monitoring throughout the product lifecycle.', link: '/services#pharmacovigilance' },
  { icon: AlertTriangle, title: 'Drug Recall Management', description: 'Rapid response and compliance for product safety issues.', link: '/services#drug-recall' }
];

const stats = [
  { value: '500+', label: 'Clinical Trials' },
  { value: '40%', label: 'Faster Timelines' },
  { value: '99.8%', label: 'Compliance Rate' },
  { value: '15+', label: 'Countries Served' }
];

const trustBadges = [
  { icon: Award, label: 'FDA Compliance Experts' },
  { icon: Shield, label: 'ICH-GCP E6 R2/R3' },
  { icon: Globe, label: 'Global Research' },
  { icon: Zap, label: 'AI-Powered Analytics' },
  { icon: Target, label: '21 CFR Part 11' },
  { icon: FileCheck, label: 'EU Annex 11' },
  { icon: Shield, label: 'ISO 13485' },
  { icon: Shield, label: 'MHRA GxP' },
  // { icon: Users, label: 'Veteran-Led Team' },
  { icon: Clock, label: '24/7 Support' },
  { icon: BarChart3, label: 'Real-Time Insights' }
];

const identificationBadges = [
  { label: 'UNIQUE ENTITY ID: VUBQWVMUCWK6' },
  { label: 'CAGE Code: 08B84' },
  { label: 'Primary NAICS: 541511 | 541714 | 541715' },
  
  { label: 'D-U-N-S: 119404620' },
  { label: 'SBA Veteran Owned' },
  { label: 'SBA Service-Disabled Veteran Owned' },
  { label: 'NVBDC Certified' },
  { label: 'NMSDC Certified' },
  { label: 'FL Department of Management Services' }
];

const advantages = [
  { icon: Users, title: 'Veteran-Led Discipline', description: 'Military precision and problem-solving expertise applied to clinical research.' },
  { icon: Zap, title: 'AI-Powered Technology', description: 'Proprietary platforms that accelerate timelines and improve data quality.' },
  { icon: Globe, title: 'Global Capabilities', description: 'Extensive network supporting trials across 15+ countries worldwide.' },
  { icon: TrendingUp, title: 'Accelerated Timelines', description: '40% faster study builds with our advanced EDC platform.' }
];

const expertise = [
  { title: 'Oncology', image: oncologyImage },
  { title: 'Rare Disease', image: rareDiseases },
  { title: 'Neuroscience', image: nuroImage },
  { title: 'Immunology', image: immunImage },
  { title: 'Cell & Gene Therapy', image: geneImage },
  { title: 'Medical Devices', image: equpImage },
  { title: 'Cardiovascular', image: cardioImage },
  { title: 'Infectious Disease', image: infuImage },
  { title: 'Dermatology', image: dermImage },
  { title: 'Gastroenterology', image: digestiveImage },
  { title: 'Ophthalmology', image: optoImage },
  { title: 'Respiratory', image: lungImage },
  { title: 'Endocrinology', image: 'https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg' },
  { title: 'Hematology', image: 'https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/5wuz3rbr_neurology-research-doctor-examining-a-human-brain-2025-04-05-07-19-24-utc.jpg' },
  { title: 'Vaccines', image: 'https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/phhm3ph8_analyzing-medical-data-on-monitors-in-modern-lab-s-2025-05-30-14-51-55-utc.jpg' },
  { title: 'Pediatrics', image: 'https://customer-assets.emergentagent.com/job_clinical-research-3/artifacts/2jmfx93h_scientists-mix-red-and-orange-chemicals-in-a-glass-2025-01-16-13-51-09-utc.jpg' }
];

export const Home = ({ onDemoClick }) => {
  const location = useLocation();

  React.useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-[#090B17] w-full" data-testid="home-page">
      {/* Hero Section - Redesigned */}
      <section className="hero-section pt-20 pb-16 min-h-[95vh] flex items-center relative w-full overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        
        {/* Background Image - Full width with overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE} 
            alt="Clinical Research" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090B17] via-[#090B17]/95 to-[#090B17]/70" />
        </div>

        <div className="w-full px-6 sm:px-12 lg:px-20 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Text Content - Takes 7 columns */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#DC2F3C]/10 border border-[#DC2F3C]/30 mb-8">
                <Shield className="w-4 h-4 text-[#DC2F3C]" />
                <span className="text-sm text-[#E8ECF2] font-medium tracking-wide">Veteran-Led Clinical Research Excellence</span>
              </div>

              <h1 className="text-[1.75rem] sm:text-[2.25rem] lg:text-[3rem] font-outfit font-bold leading-[1.2] mb-6" data-testid="hero-title">
                <span className="block">Innovative Research Solutions</span>
                <span className="block">for a <span className="text-gradient">Smarter and Faster</span></span>
                <span className="block">Path to Approval</span>
              </h1>

              <p className="text-sm sm:text-base mb-10 leading-relaxed max-w-xl text-[#B0B5BD]">
                Helping life science innovators turn breakthrough science into life-changing 
                drugs, devices, and diagnostics. Our veteran-led team delivers precision, 
                compliance, and innovation at every stage.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <button 
                  onClick={onDemoClick}
                  className="btn-gradient px-10 py-4 text-base font-semibold flex items-center justify-center gap-2 select-none"
                  data-testid="hero-demo-btn"
                >
                  Contact Us
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a 
                  href={capabilitiesPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-outline px-10 py-4 text-base font-semibold flex items-center justify-center gap-2 select-none"
                  data-testid="hero-services-btn"
                >
                  Explore Capabilities
                </a>
              </div>

              {/* Quick Stats - Horizontal */}
              <div className="flex flex-wrap gap-8 lg:gap-12 pt-8 border-t border-[#2E3446]/50">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 + index * 0.1 }}
                    className="text-left"
                  >
                    <div className="stat-value text-3xl sm:text-4xl mb-1">{stat.value}</div>
                    <div className="text-xs text-[#9CA3AF] uppercase tracking-wider font-medium">{stat.label}</div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Hero Image - Takes 5 columns */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="lg:col-span-5 hidden lg:block"
            >
              <div className="relative">
                {/* Main Image */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-[#2E3446]">
                  <img 
                    src={HERO_IMAGE} 
                    alt="Clinical Research" 
                    className="w-full h-[500px] object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#090B17]/60 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card - Bottom Left */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.9 }}
                  className="absolute -bottom-6 -left-6 bg-[#1B1F2C] rounded-xl p-5 border border-[#2E3446] shadow-xl"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#DC2F3C]/10 border border-[#DC2F3C]/30 flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-[#DC2F3C]" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-[#DC2F3C]">99.8%</div>
                      <div className="text-sm text-[#9CA3AF]">Compliance Rate</div>
                    </div>
                  </div>
                </motion.div>

                {/* Floating Card - Top Right */}
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                  className="absolute -top-4 -right-4 bg-[#1B1F2C] rounded-xl p-4 border border-[#2E3446] shadow-xl"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#DC2F3C]/10 border border-[#DC2F3C]/30 flex items-center justify-center">
                      <Globe className="w-5 h-5 text-[#DC2F3C]" />
                    </div>
                    <div>
                      <div className="text-xl font-bold text-white">15+</div>
                      <div className="text-xs text-[#9CA3AF]">Countries</div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Badges Marquee */}
      <section className="section-surface border-y border-[#2E3446]">
        <div className="marquee-container">
          <div className="marquee-content">
            {[...trustBadges, ...trustBadges].map((badge, index) => (
              <div key={index} className="trust-badge">
                <badge.icon className="w-5 h-5" />
                <span className="font-medium text-white">{badge.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Identification & Certifications Marquee */}
      <section className="bg-[#0D1020] border-y border-[#2E3446]">
        <div className="marquee-container">
          <div className="marquee-content-slow">
            {[...identificationBadges, ...identificationBadges].map((badge, index) => (
              <div key={index} className="id-badge">
                <span className="font-medium text-[#9CA3AF] mr-6">{badge.label}  </span>
               </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section-dark py-24 scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="section-label">Our Capabilities</span>
            <h2 className="section-title mt-3">The Pathway to <span className="text-gradient">Approval</span></h2>
            <p className="section-subtitle mx-auto mt-4">
              Wherever you are in the development lifecycle, our clinical research and consulting 
              experts deliver strategic, technology-powered solutions to accelerate your path.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={service.link}
                  className="service-card block h-full group"
                  data-testid={`service-card-${index}`}
                >
                  <div className="icon-box mb-5">
                    <service.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-silver-shine group-hover:text-gradient transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-[#B0B5BD] text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="text-gradient text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                    Learn More <ArrowRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/services" 
              className="btn-gradient px-8 py-4 text-base inline-flex items-center gap-2 select-none"
              data-testid="explore-capabilities-btn"
            >
              Explore All Capabilities
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Expertise Section - Scrolling Marquee */}
      <section id="expertise" className="section-surface py-24 border-y border-[#2E3446] scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20 mb-12">
          <div className="text-center">
            <span className="section-label">Therapeutic Areas</span>
            <h2 className="section-title mt-3">Expertise That <span className="text-gradient">Matters</span></h2>
            <p className="section-subtitle mx-auto mt-4">
              Clinical research is advancing at an extraordinary pace. Explore how our therapeutic 
              expertise drives the strategy to bring your innovation to market.
            </p>
          </div>
        </div>

        {/* Scrolling Therapeutic Areas */}
        <div className="expertise-marquee-container overflow-hidden">
          <div className="expertise-marquee-content flex gap-6 animate-expertise-scroll">
            {[...expertise, ...expertise].map((item, index) => (
              <div
                key={`${item.title}-${index}`}
                className="expertise-card-scroll flex-shrink-0 w-48 h-48 relative rounded-xl overflow-hidden group"
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090B17]/90 via-[#090B17]/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
                  <h3 className="text-white font-semibold text-sm">{item.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Section */}
      <section id="technology" className="section-dark py-24 scroll-mt-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Technology</span>
              <h2 className="section-title mt-3 mb-6">AI-Powered Clinical <span className="text-gradient">Innovation</span></h2>
              <p className="text-[#C9C9CD] text-base leading-relaxed mb-8">
                Our proprietary technology platforms leverage artificial intelligence and 
                advanced analytics to streamline clinical operations, reduce timelines, and 
                improve data quality.
              </p>

              <div className="space-y-4">
                <div className="card-gradient p-5">
                  <h4 className="font-semibold mb-2 text-silver-shine">Sclin EDC Platform</h4>
                  <p className="text-sm text-[#C9C9CD]">
                    Next-generation Electronic Data Capture with 40% faster build time, 
                    real-time validation, and seamless integration capabilities.
                  </p>
                </div>
                <div className="card-gradient p-5">
                  <h4 className="font-semibold mb-2 text-silver-shine">DocuDefender eTMF</h4>
                  <p className="text-sm text-[#C9C9CD]">
                    AI-powered Trial Master File management with automated document indexing, 
                    ensuring inspection-readiness at all times.
                  </p>
                </div>
              </div>

              <Link 
                to="/technology" 
                className="btn-gradient inline-flex items-center gap-2 px-6 py-3 mt-8 select-none"
                data-testid="explore-tech-btn"
              >
                Explore Technology
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden glow-subtle">
                <img 
                  src={TECH_IMAGE} 
                  alt="Technology" 
                  className="w-full"
                />
              </div>
              
              {/* Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-8 -right-8 gradient-border p-6 hidden sm:block"
              >
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="stat-value text-2xl">40%</div>
                    <div className="text-xs text-[#C9C9CD]">Faster Builds</div>
                  </div>
                  <div className="text-center">
                    <div className="stat-value text-2xl">99.9%</div>
                    <div className="text-xs text-[#C9C9CD]">Uptime</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Advantages Section */}
      <section className="section-surface py-24 border-y border-[#2E3446]">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="text-center mb-16">
            <span className="section-label">Why Choose Us</span>
            <h2 className="section-title mt-3">The VetForce <span className="text-gradient">Advantage</span></h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="icon-box mx-auto mb-5">
                  <item.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-silver-shine">{item.title}</h3>
                <p className="text-sm text-[#C9C9CD]">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="section-dark py-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="section-label">Careers</span>
              <h2 className="section-title mt-3 mb-6">Build Your Future with <span className="text-gradient">VetForce</span></h2>
              <p className="text-[#C9C9CD] mb-6">
                Our strength lies in our people. At VetForce, your ideas count, your growth is 
                supported, and your impact is real. Join a purpose-driven team that's transforming 
                clinical development.
              </p>
              
              <div className="space-y-3 mb-8">
                {['Competitive compensation & benefits', 'Professional development programs', 'Veteran-friendly workplace', 'Remote & flexible work options'].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#8F262B] flex-shrink-0" />
                    <span className="text-[#C9C9CD]">{item}</span>
                  </div>
                ))}
              </div>

              <Link 
                to="/careers" 
                className="btn-gradient px-6 py-3 inline-flex items-center gap-2 select-none"
                data-testid="careers-link-btn"
              >
                View Open Positions
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[Building2, Users, Award, Globe].map((Icon, index) => (
                <div key={index} className="card-dark p-6">
                  <div className="icon-box mb-4">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="stat-value text-2xl mb-1">
                    {['50+', '200+', '15+', '10+'][index]}
                  </div>
                  <div className="text-sm text-[#C9C9CD]">
                    {['Active Studies', 'Team Members', 'Countries', 'Years Experience'][index]}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="w-full px-6 sm:px-12 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="cta-gradient rounded-2xl p-12 text-center relative"
          >
            <h2 className="text-3xl sm:text-4xl font-outfit font-bold text-silver-shine mb-4">
              Ready to Get Started? <span className="text-gradient">So Are We.</span>
            </h2>
            <p className="text-lg text-[#C9C9CD] max-w-xl mx-auto mb-8">
              Drop us a line to learn more about how we can help accelerate your clinical research.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={onDemoClick}
                className="btn-gradient px-8 py-4 text-base select-none"
                data-testid="cta-demo-btn"
              >
                Connect With Us
              </button>
              <Link 
                to="/contact" 
                className="btn-outline px-8 py-4 text-base select-none"
                data-testid="cta-contact-btn"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
